import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import defaultLogo from "$/defaultLogo.svg";
import {
  useCustomerFeed,
  type FeedItem,
  type FeedPhoto,
} from "~/services/customer/feed";

export const Route = createFileRoute("/customer/layout/feed")({
  component: CustomerFeed,
});

const fmtDate = (iso?: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getMonth() + 1}월 ${d.getDate()}일`;
};

const photoSrc = (p: FeedPhoto) => p.thumbnailUrl || p.fileUrl || "";

function PhotoGrid({
  photos,
  onOpen,
}: {
  photos: FeedPhoto[];
  onOpen: (index: number) => void;
}) {
  if (photos.length === 0) return null;

  if (photos.length === 1) {
    return (
      <button
        type="button"
        onClick={() => onOpen(0)}
        className="block w-full overflow-hidden bg-gray-100"
      >
        <img
          src={photoSrc(photos[0])}
          alt=""
          className="max-h-[420px] w-full object-cover"
        />
      </button>
    );
  }

  const shown = photos.slice(0, 3);
  return (
    <div
      className={`grid gap-0.5 ${photos.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}
    >
      {shown.map((p, i) => {
        const isLast = i === 2 && photos.length > 3;
        return (
          <button
            key={p.fileSeq}
            type="button"
            onClick={() => onOpen(i)}
            className="relative aspect-square overflow-hidden bg-gray-100"
          >
            <img
              src={photoSrc(p)}
              alt=""
              className="h-full w-full object-cover"
            />
            {isLast && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-lg font-semibold text-white">
                +{photos.length - 3}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function FeedCard({
  feed,
  onOpenPhoto,
}: {
  feed: FeedItem;
  onOpenPhoto: (photos: FeedPhoto[], index: number) => void;
}) {
  return (
    <article className="overflow-hidden rounded-2xl border border-[#EDEDED] bg-white">
      <div className="flex items-center gap-2.5 px-3.5 py-3">
        <img
          src={feed.companyFileUrl || defaultLogo}
          alt=""
          className="h-9 w-9 rounded-full border border-[#F0F0F0] object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = defaultLogo;
          }}
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-semibold text-[#222]">
            {feed.companyName || "우리 매장"}
          </div>
          <div className="text-[11px] text-[#999]">
            {fmtDate(feed.dtRegist)}
          </div>
        </div>
        {feed.dtEvent && (
          <span className="shrink-0 rounded-full bg-[#FFE7CC] px-2.5 py-1 text-[11px] font-medium text-[#B36414]">
            행사 {feed.dtEvent.slice(5).replace("-", ".")}
          </span>
        )}
      </div>

      <PhotoGrid
        photos={feed.photos}
        onOpen={(i) => onOpenPhoto(feed.photos, i)}
      />

      {feed.content && (
        <p className="whitespace-pre-wrap px-3.5 py-3 text-[14px] leading-relaxed text-[#333]">
          {feed.content}
        </p>
      )}
    </article>
  );
}

function Lightbox({
  photos,
  index,
  onClose,
  onIndex,
}: {
  photos: FeedPhoto[];
  index: number;
  onClose: () => void;
  onIndex: (i: number) => void;
}) {
  const p = photos[index];
  if (!p) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="닫기"
        className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-xl text-white"
      >
        ×
      </button>
      <img
        src={p.fileUrl || photoSrc(p)}
        alt=""
        className="max-h-[85vh] max-w-[92vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onIndex((index - 1 + photos.length) % photos.length);
            }}
            aria-label="이전"
            className="absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-2xl text-white"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onIndex((index + 1) % photos.length);
            }}
            aria-label="다음"
            className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-2xl text-white"
          >
            ›
          </button>
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/15 px-3 py-1 text-xs text-white">
            {index + 1} / {photos.length}
          </div>
        </>
      )}
    </div>
  );
}

function CustomerFeed() {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCustomerFeed();
  const [box, setBox] = useState<{ photos: FeedPhoto[]; index: number } | null>(
    null,
  );

  const feeds = useMemo<FeedItem[]>(
    () => (data?.pages ?? []).flatMap((p) => p.content ?? []),
    [data],
  );

  return (
    <>
      <LayoutHeader title={"소식"} hasBackBtn hasFootPrint={false} />
      <div className="flex flex-col flex-auto min-h-full w-full overflow-x-hidden bg-[#F5F5F5] px-3 pb-8 pt-4">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-sm text-[#666]">
            불러오는 중...
          </div>
        ) : feeds.length === 0 ? (
          <div className="flex h-52 flex-col items-center justify-center gap-2 text-center text-sm text-[#888]">
            <span className="text-3xl" aria-hidden>
              📷
            </span>
            이용하는 매장의 소식이 아직 없어요.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {feeds.map((feed) => (
              <FeedCard
                key={feed.feedSeq}
                feed={feed}
                onOpenPhoto={(photos, index) => setBox({ photos, index })}
              />
            ))}
            {hasNextPage && (
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="mt-1 w-full rounded-xl border border-[#E5E5E5] bg-white py-3 text-sm text-[#666]"
              >
                {isFetchingNextPage ? "불러오는 중…" : "이전 소식 더 보기"}
              </button>
            )}
          </div>
        )}
      </div>

      {box && (
        <Lightbox
          photos={box.photos}
          index={box.index}
          onClose={() => setBox(null)}
          onIndex={(i) => setBox({ photos: box.photos, index: i })}
        />
      )}
    </>
  );
}
